import React, { useState, useEffect } from 'react';

const PaymentMethodReport = ({ selectedTenant }) => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({ users: [], pcs: [] });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPC, setSelectedPC] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const API_BASE = 'https://teranetsystemspos.venvirtual.net/backend-php/reports_payment_method.php';

  useEffect(() => {
    loadReport();
  }, [selectedTenant, dateFrom, dateTo, selectedUser, selectedPC]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: 'payment-method-report',
        tenant_id: selectedTenant?.id || 1,
        date_from: dateFrom,
        date_to: dateTo,
        user_name: selectedUser,
        pc_name: selectedPC
      });

      const response = await fetch(`${API_BASE}?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data || []);
        setSummary(result.summary || {});
        setFilters(result.filters || { users: [], pcs: [] });
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const prefix = currency === 'USD' ? '$' : 'Bs ';
    return prefix + (amount || 0).toFixed(2);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInvoiceTypeColor = (type) => {
    const colors = {
      'Factura': 'text-green-600',
      'Dev Factura': 'text-red-600',
      'Voucher': 'text-blue-600',
      'Dev Voucher': 'text-orange-600'
    };
    return colors[type] || 'text-gray-600';
  };

  const getOrderTypeText = (type) => {
    const types = {
      'cash': 'Contado',
      'credit': 'Crédito'
    };
    return types[type?.toLowerCase()] || type || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-lg">Cargando reporte...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Ventas x Voucher x Forma de Pago</h2>
          <p className="text-gray-600 mt-1">Reporte detallado de ventas por método de pago</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                {filters.users.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Equipo</label>
              <select
                value={selectedPC}
                onChange={(e) => setSelectedPC(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                {filters.pcs.map(pc => (
                  <option key={pc} value={pc}>{pc}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-600">Total Transacciones</h3>
          <p className="text-2xl font-bold text-blue-600">{summary.total_transactions || 0}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-600">Total USD</h3>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_sales_usd, 'USD')}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-600">Total BS</h3>
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(summary.total_sales_bs, 'BS')}</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-600">Por Cobrar</h3>
          <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.total_due_usd, 'USD')}</p>
        </div>
      </div>

      {/* Payment Method Breakdown */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">Resumen por Método de Pago</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Efectivo USD</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_cash_usd, 'USD')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Zille USD</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_zille_usd, 'USD')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Transfer USD</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_transfer_usd, 'USD')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Tarjeta USD</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_credit_usd, 'USD')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Efectivo BS</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_cash_bs, 'BS')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Débito BS</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_debit_bs, 'BS')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Transfer BS</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_transfer_bs, 'BS')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-sm text-gray-600">Tarjeta BS</div>
            <div className="text-lg font-bold">{formatCurrency(summary.total_credit_bs, 'BS')}</div>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Venta</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total BS</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Efectivo USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Zille USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Transfer USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tarjeta USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Efectivo BS</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Débito BS</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Transfer BS</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tarjeta BS</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Recibido USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Recibido BS</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Restante USD</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Restante BS</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Equipo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm">{formatDate(row.date_formatted)}</td>
                <td className={`px-4 py-2 text-sm font-medium ${getInvoiceTypeColor(row.InvoiceType)}`}>
                  {row.InvoiceType}
                </td>
                <td className="px-4 py-2 text-sm">{row.InvoiceNo}</td>
                <td className="px-4 py-2 text-sm">{getOrderTypeText(row.OrderType)}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.TotalInUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.TotalInBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.CashUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.ZilleUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.TransferUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.CreditUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.CashBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.DebitBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.TransferBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.CreditBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm text-right text-green-600">{formatCurrency(row.RcvdUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right text-green-600">{formatCurrency(row.RcvdBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm text-right text-red-600">{formatCurrency(row.DueUSD, 'USD')}</td>
                <td className="px-4 py-2 text-sm text-right text-red-600">{formatCurrency(row.DueBS, 'BS')}</td>
                <td className="px-4 py-2 text-sm">{row.UserName}</td>
                <td className="px-4 py-2 text-sm">{row.PCName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No se encontraron datos para el período seleccionado
        </div>
      )}
    </div>
  );
};

export default PaymentMethodReport;
